import { iot, mqtt5 } from "aws-crt/dist.browser/browser";
import mitt from "mitt";
import { VelavuStreamUpdateOperation } from "./streamUpdate";
import { VelavuAPI } from "../dao/velavu-api";
const RETRY_DELAY = 1000;
//Re-export the CredentialsProvider type from aws-crt
export { CredentialsProvider as VelavuEventStreamCredentialsProvider } from "aws-crt/dist.browser/browser/auth";
function initializeClient(config, emitter) {
    const mqttConfig = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(config.mqttEndpoint ?? "iot.velavu.com", {
        credentialsProvider: config.credentialsProvider,
        region: config.awsRegion
    }).build();
    const client = new mqtt5.Mqtt5Client(mqttConfig);
    client.on("connectionSuccess", async () => {
        console.debug("Connected to Velavu event stream over MQTT");
        //When we connect, subscribe to topics
        let suback;
        try {
            suback = await client.subscribe({
                subscriptions: [
                    {
                        qos: mqtt5.QoS.AtLeastOnce,
                        topicFilter: `events/${config.customerID}/#`,
                    }
                ]
            });
        }
        catch (err) {
            console.warn("Failed to subscribe to Velavu event stream topics:", err);
            return;
        }
        //Check if subscription was successful
        if (suback.reasonCodes && suback.reasonCodes.length > 0) {
            const reasonCode = suback.reasonCodes[0];
            if (reasonCode >= 0x80) {
                console.warn("Failed to subscribe to Velavu event stream topics. Reason code:", reasonCode, "Message:", suback.reasonString);
                return;
            }
        }
    });
    const decoder = new TextDecoder("utf8");
    client.on("messageReceived", (event) => {
        //Retrieve the event type and ID
        const topicSplit = event.message.topicName.split("/");
        // The topic format is as follows:
        // events/.customer_id/.table_name/.object_id/.operation_name
        if (topicSplit.length < 5) {
            console.warn("Received MQTT update with an invalid topic:", event.message.topicName);
            return;
        }
        const tableName = topicSplit[2];
        const objectID = topicSplit[3];
        const operationName = topicSplit[4];
        //Parse the payload
        let payload;
        if (operationName === VelavuStreamUpdateOperation.Deleted) {
            payload = null;
        }
        else {
            const rawPayload = event.message.payload;
            if (rawPayload === undefined) {
                return;
            }
            payload = JSON.parse(decoder.decode(rawPayload));
        }
        //Log the event
        console.debug("Received MQTT update", {
            table: tableName,
            objectID: objectID,
            operation: operationName,
            payload: payload
        });
        //Emit the event
        emitter.emit(tableName, {
            operation: operationName,
            id: objectID,
            body: payload
        });
    });
    //Log errors
    client.on("error", (error) => {
        console.warn("MQTT client error:", error);
    });
    return client;
}
export class VelavuEventStream {
    #configProvider;
    #isRegisteredWithMQTT = false;
    #isRunning = false;
    #client = null;
    events = mitt();
    constructor(configProvider) {
        this.#configProvider = configProvider;
    }
    start() {
        if (this.#isRunning) {
            return;
        }
        this.#isRunning = true;
        this.#startImpl();
    }
    async #startImpl() {
        //Fetch the config
        let config = null;
        while (config === null) {
            if (!this.#isRunning) {
                return;
            }
            try {
                config = await this.#configProvider();
            }
            catch (err) {
                console.warn("Failed to fetch event stream config:", err);
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            }
        }
        //Initialize the client
        if (this.#client === null) {
            this.#client = initializeClient(config, this.events);
        }
        //Register with MQTT
        while (!this.#isRegisteredWithMQTT) {
            if (!this.#isRunning) {
                return;
            }
            try {
                await VelavuAPI.authorizeMQTT.authorizeMQTT(config.awsIdentityID);
                this.#isRegisteredWithMQTT = true;
            }
            catch (err) {
                console.warn("Failed to register with MQTT:", err);
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            }
        }
        if (!this.#isRunning) {
            return;
        }
        //Start the client
        this.#client.start();
        console.debug("Started Velavu event stream over MQTT");
    }
    stop() {
        if (!this.#isRunning) {
            return;
        }
        this.#client?.stop();
        this.#isRunning = false;
    }
}
//# sourceMappingURL=stream.js.map